import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tenantAPI, Tenant } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/LanguageContext';
import Icon from '../components/Icon';

/**
 * Super Admin: per-tenant SES (email) and NTH (SMS/Viber) credentials.
 *
 * Secret values (AWS keys, NTH API key) are NEVER returned by the API.
 * The inputs are therefore left blank and only sent when the admin types a
 * new value — leaving them empty keeps the existing stored secret unchanged.
 */
const TenantConfig: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const t = useT();
  const tenantId = parseInt(id!);

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // SES (AWS) — secrets blank by design
  const [ses, setSes] = useState({
    awsRegion: '',
    awsAccessKeyId: '',
    awsSecretAccessKey: '',
    awsSesFromEmail: '',
    awsSesFromName: '',
    sesRateLimit: 14,
  });

  // NTH (SMS / Viber) — secret blank by design.
  // NTH uses HTTP Basic auth: nthUsername + nthApiKey (password).
  const [nth, setNth] = useState({
    nthUsername: '',
    nthApiKey: '',
    nthApiUrl: '',
    nthRateLimit: 50,
    nthFromName: '',
  });

  // Which delivery channels this tenant is allowed to use (Tenant attributes).
  const [channels, setChannels] = useState({ email: true, sms: true, viber: true });
  const [smsFallback, setSmsFallback] = useState(false);
  const [messagesOverview, setMessagesOverview] = useState(false);
  const [pricing, setPricing] = useState({ priceEmail: 1.4, priceSms: 1.4, priceViber: 1.4, serviceFee: 3000 });

  useEffect(() => {
    loadTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTenant = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await tenantAPI.getOne(tenantId);
      if (res.data.success) {
        const t = res.data.data.tenant;
        setTenant(t);
        setChannels({
          email: t.emailEnabled ?? true,
          sms: t.smsEnabled ?? true,
          viber: t.viberEnabled ?? true,
        });
        setSmsFallback(t.smsFallbackEnabled ?? false);
        setMessagesOverview(t.messagesOverviewEnabled ?? false);
        setPricing({
          priceEmail: Number(t.priceEmail ?? 1.4),
          priceSms: Number(t.priceSms ?? 1.4),
          priceViber: Number(t.priceViber ?? 1.4),
          serviceFee: Number(t.serviceFee ?? 3000),
        });
        const cfg = t.config || {};
        setSes((prev) => ({
          ...prev,
          awsRegion: cfg.awsRegion || '',
          awsSesFromEmail: cfg.awsSesFromEmail || '',
          awsSesFromName: cfg.awsSesFromName || '',
          sesRateLimit: cfg.sesRateLimit ?? 14,
        }));
        setNth((prev) => ({
          ...prev,
          nthUsername: cfg.nthUsername || '',
          nthApiUrl: cfg.nthApiUrl || '',
          nthRateLimit: cfg.nthRateLimit ?? 50,
          nthFromName: cfg.nthFromName || '',
        }));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('Učitavanje klijenta nije uspelo', 'Failed to load tenant'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    // Only send secret fields when the admin actually typed something,
    // so blank inputs don't wipe existing stored credentials.
    const payload: any = {
      awsRegion: ses.awsRegion || undefined,
      awsSesFromEmail: ses.awsSesFromEmail || undefined,
      awsSesFromName: ses.awsSesFromName || undefined,
      sesRateLimit: ses.sesRateLimit,
      nthUsername: nth.nthUsername || undefined,
      nthApiUrl: nth.nthApiUrl || undefined,
      nthRateLimit: nth.nthRateLimit,
      nthFromName: nth.nthFromName || undefined,
    };
    if (ses.awsAccessKeyId) payload.awsAccessKeyId = ses.awsAccessKeyId;
    if (ses.awsSecretAccessKey) payload.awsSecretAccessKey = ses.awsSecretAccessKey;
    if (nth.nthApiKey) payload.nthApiKey = nth.nthApiKey;

    try {
      // Channel permissions live on the Tenant model (separate endpoint).
      await tenantAPI.update(tenantId, {
        emailEnabled: channels.email,
        smsEnabled: channels.sms,
        viberEnabled: channels.viber,
        smsFallbackEnabled: smsFallback,
        messagesOverviewEnabled: messagesOverview,
        priceEmail: pricing.priceEmail,
        priceSms: pricing.priceSms,
        priceViber: pricing.priceViber,
        serviceFee: pricing.serviceFee,
      });
      const res = await tenantAPI.updateConfig(tenantId, payload);
      if (res.data.success) {
        setSuccess(t('Sačuvano (tajne se čuvaju šifrovano).', 'Saved (secrets are stored encrypted).'));
        // Clear secret inputs after save
        setSes((prev) => ({ ...prev, awsAccessKeyId: '', awsSecretAccessKey: '' }));
        setNth((prev) => ({ ...prev, nthApiKey: '' }));
        loadTenant();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('Čuvanje kredencijala nije uspelo', 'Failed to save credentials'));
    } finally {
      setSaving(false);
    }
  };

  if (role !== 'super_admin') {
    return (
      <div className="page">
        <div
          className="card card-pad"
          style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)' }}
        >
          <strong>{t('Pristup odbijen.', 'Access denied.')}</strong> {t('Ova stranica je dostupna samo super administratorima.', 'This page is only available to super administrators.')}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => navigate('/admin/tenants')}
        style={{ marginBottom: 14, paddingLeft: 6 }}
      >
        <Icon name="chevronLeft" size={16} /> {t('Nazad na klijente', 'Back to tenants')}
      </button>

      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Kredencijali', 'Credentials')}{tenant ? ` — ${tenant.name}` : ''}</h1>
          <p className="page-sub">{t('AWS SES (Email) i NTH (SMS i Viber) kredencijali po klijentu.', 'Per-tenant AWS SES (Email) and NTH (SMS & Viber) credentials.')}</p>
        </div>
      </div>

      {error && (
        <div
          className="card card-pad"
          style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 16 }}
        >
          <Icon name="alert" size={16} /> {error}
        </div>
      )}
      {success && (
        <div
          className="card card-pad"
          style={{ borderColor: 'var(--ok)', color: 'var(--brand-700)', marginBottom: 16 }}
        >
          <Icon name="checkCircle" size={16} /> {success}
        </div>
      )}

      <div
        className="card card-pad flex items-center gap-12"
        style={{
          background: 'var(--accent-soft)',
          borderColor: 'var(--warn)',
          color: 'var(--text)',
          marginBottom: 20,
          fontSize: 13.5,
        }}
      >
        <span style={{ color: 'var(--warn)', flexShrink: 0 }}>
          <Icon name="shield" size={18} />
        </span>
        <span>
          {t('Tajna polja (AWS ključevi, NTH API ključ) čuvaju se', 'Secret fields (AWS keys, NTH API key) are stored')}{' '}
          <strong>{t('šifrovano', 'encrypted')}</strong>{' '}
          {t(
            'i nikada se ne vraćaju u pregledač. Ostavite ih prazna da biste zadržali trenutnu vrednost; unesite novu vrednost samo da biste je promenili.',
            'and are never sent back to the browser. Leave them blank to keep the current value; type a new value only to change it.'
          )}
        </span>
      </div>

      {loading ? (
        <div className="card card-pad muted">{t('Učitavanje…', 'Loading…')}</div>
      ) : (
        <form onSubmit={handleSave}>
          {/* Allowed channels */}
          <div className="card card-pad" style={{ marginBottom: 20 }}>
            <div className="card-title" style={{ marginBottom: 6 }}>{t('Dozvoljeni kanali', 'Allowed channels')}</div>
            <div className="hint" style={{ marginBottom: 14 }}>
              {t('Odredite koje kanale ovaj klijent može da koristi. Isključeni kanali se ne prikazuju pri kreiranju kampanje.', 'Choose which channels this tenant may use. Disabled channels are hidden when creating a campaign.')}
            </div>
            {([
              ['email', t('Email', 'Email')],
              ['sms', 'SMS'],
              ['viber', 'Viber'],
            ] as const).map(([key, label]) => (
              <label key={key} className="flex items-center gap-8" style={{ cursor: 'pointer', userSelect: 'none', padding: '6px 0' }}>
                <input
                  type="checkbox"
                  className="row-check"
                  checked={channels[key]}
                  onChange={(e) => setChannels({ ...channels, [key]: e.target.checked })}
                />
                <span style={{ fontWeight: 600 }}>{label}</span>
              </label>
            ))}
          </div>

          {/* SMS failover (Viber) — admin gate */}
          <div className="card card-pad" style={{ marginBottom: 20 }}>
            <div className="card-title" style={{ marginBottom: 6 }}>{t('SMS rezerva (Viber)', 'SMS failover (Viber)')}</div>
            <div className="hint" style={{ marginBottom: 14 }}>
              {t('Ako je uključeno, kad klijent u Viber kampanji definiše SMS rezervu, ona se zaista i šalje kao failover. Ako je isključeno, klijent može da je podesi, ali se SMS rezerva ne šalje. Ne utiče na čiste SMS kampanje ni na test SMS.', 'When on, a Viber campaign\'s SMS failover actually fires if the client defined it. When off, the client can configure it but no failover SMS is sent. Does not affect pure SMS campaigns or SMS tests.')}
            </div>
            <label className="flex items-center gap-8" style={{ cursor: 'pointer', userSelect: 'none', padding: '6px 0' }}>
              <input
                type="checkbox"
                className="row-check"
                checked={smsFallback}
                onChange={(e) => setSmsFallback(e.target.checked)}
              />
              <span style={{ fontWeight: 600 }}>{t('Dozvoli SMS failover za ovog klijenta', 'Allow SMS failover for this tenant')}</span>
            </label>
          </div>

          {/* Messages overview — admin gate */}
          <div className="card card-pad" style={{ marginBottom: 20 }}>
            <div className="card-title" style={{ marginBottom: 6 }}>{t('Pregled poruka', 'Messages overview')}</div>
            <div className="hint" style={{ marginBottom: 14 }}>
              {t('Ako je uključeno, korisnici ovog klijenta vide stranicu „Pregled poruka" sa svim poslatim porukama i mogu da otvore pregled svake poruke. Super administrator je uvek vidi.', 'When on, this tenant\'s users can see the "Messages" page listing all sent messages and open a preview of each. Super admins always see it.')}
            </div>
            <label className="flex items-center gap-8" style={{ cursor: 'pointer', userSelect: 'none', padding: '6px 0' }}>
              <input
                type="checkbox"
                className="row-check"
                checked={messagesOverview}
                onChange={(e) => setMessagesOverview(e.target.checked)}
              />
              <span style={{ fontWeight: 600 }}>{t('Dozvoli klijentu pregled poslatih poruka', 'Allow this tenant to view sent messages')}</span>
            </label>
          </div>

          {/* Pricing (RSD) — used by the spend report */}
          <div className="card card-pad" style={{ marginBottom: 20 }}>
            <div className="card-title" style={{ marginBottom: 6 }}>{t('Cenovnik (RSD)', 'Pricing (RSD)')}</div>
            <div className="hint" style={{ marginBottom: 14 }}>
              {t('Cena po poruci po kanalu i fiksna naknada za uslugu. Koristi se u izveštaju potrošnje.', 'Per-message price per channel and a fixed service fee. Used by the spend report.')}
            </div>
            <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
              <div className="field">
                <label className="label">{t('Email (po poruci)', 'Email (per message)')}</label>
                <input className="input" type="number" step="0.01" value={pricing.priceEmail}
                  onChange={(e) => setPricing({ ...pricing, priceEmail: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="field">
                <label className="label">{t('SMS (po poruci)', 'SMS (per message)')}</label>
                <input className="input" type="number" step="0.01" value={pricing.priceSms}
                  onChange={(e) => setPricing({ ...pricing, priceSms: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="field">
                <label className="label">{t('Viber (po poruci)', 'Viber (per message)')}</label>
                <input className="input" type="number" step="0.01" value={pricing.priceViber}
                  onChange={(e) => setPricing({ ...pricing, priceViber: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="field">
                <label className="label">{t('Naknada za uslugu (fiksno)', 'Service fee (fixed)')}</label>
                <input className="input" type="number" step="1" value={pricing.serviceFee}
                  onChange={(e) => setPricing({ ...pricing, serviceFee: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
          </div>

          {/* SES / Email */}
          <div className="card card-pad" style={{ marginBottom: 20 }}>
            <div className="flex items-center gap-12" style={{ marginBottom: 18 }}>
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'var(--ch-email-soft)',
                  color: 'var(--ch-email)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name="mail" size={18} />
              </span>
              <div className="card-title">{t('AWS SES (Email)', 'AWS SES (Email)')}</div>
            </div>

            <div className="field">
              <label className="label">{t('AWS Region', 'AWS Region')}</label>
              <input
                className="input"
                value={ses.awsRegion}
                onChange={(e) => setSes({ ...ses, awsRegion: e.target.value })}
                placeholder="us-east-1"
              />
            </div>

            <div className="field">
              <label className="label">{t('AWS Access Key ID', 'AWS Access Key ID')}</label>
              <input
                className="input"
                value={ses.awsAccessKeyId}
                onChange={(e) => setSes({ ...ses, awsAccessKeyId: e.target.value })}
                placeholder={t('•••••••• (ostavite prazno da zadržite)', '•••••••• (leave blank to keep)')}
                autoComplete="off"
              />
            </div>

            <div className="field">
              <label className="label">{t('AWS Secret Access Key', 'AWS Secret Access Key')}</label>
              <input
                className="input"
                type="password"
                value={ses.awsSecretAccessKey}
                onChange={(e) => setSes({ ...ses, awsSecretAccessKey: e.target.value })}
                placeholder={t('•••••••• (ostavite prazno da zadržite)', '•••••••• (leave blank to keep)')}
                autoComplete="new-password"
              />
            </div>

            <div className="field">
              <label className="label">{t('Email pošiljaoca', 'From Email')}</label>
              <input
                className="input"
                value={ses.awsSesFromEmail}
                onChange={(e) => setSes({ ...ses, awsSesFromEmail: e.target.value })}
                placeholder="noreply@acme.com"
              />
            </div>

            <div className="field">
              <label className="label">{t('Ime pošiljaoca', 'From Name')}</label>
              <input
                className="input"
                value={ses.awsSesFromName}
                onChange={(e) => setSes({ ...ses, awsSesFromName: e.target.value })}
                placeholder="Acme"
              />
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">{t('SES ograničenje brzine (emailova/sek)', 'SES Rate Limit (emails/sec)')}</label>
              <input
                className="input"
                type="number"
                min={1}
                value={ses.sesRateLimit}
                onChange={(e) => setSes({ ...ses, sesRateLimit: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>

          {/* NTH / SMS + Viber */}
          <div className="card card-pad" style={{ marginBottom: 20 }}>
            <div className="flex items-center gap-12" style={{ marginBottom: 8 }}>
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'var(--ch-viber-soft)',
                  color: 'var(--ch-viber)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name="phone" size={18} />
              </span>
              <div className="card-title">{t('NTH (SMS i Viber)', 'NTH (SMS & Viber)')}</div>
            </div>
            <div className="hint" style={{ marginBottom: 16 }}>
              {t(
                'NTH OmniChannel koristi HTTP Basic autentifikaciju — unesite korisničko ime i API ključ (lozinku).',
                'NTH OmniChannel uses HTTP Basic auth — enter the username and the API key (password).'
              )}
            </div>

            <div className="field">
              <label className="label">{t('NTH korisničko ime', 'NTH Username')}</label>
              <input
                className="input"
                value={nth.nthUsername}
                onChange={(e) => setNth({ ...nth, nthUsername: e.target.value })}
                placeholder={t('vaše NTH korisničko ime', 'your NTH username')}
                autoComplete="off"
              />
            </div>

            <div className="field">
              <label className="label">{t('NTH API ključ (lozinka)', 'NTH API Key (password)')}</label>
              <input
                className="input"
                type="password"
                value={nth.nthApiKey}
                onChange={(e) => setNth({ ...nth, nthApiKey: e.target.value })}
                placeholder={t('•••••••• (ostavite prazno da zadržite)', '•••••••• (leave blank to keep)')}
                autoComplete="new-password"
              />
            </div>

            <div className="field">
              <label className="label">{t('NTH API URL', 'NTH API URL')}</label>
              <input
                className="input"
                value={nth.nthApiUrl}
                onChange={(e) => setNth({ ...nth, nthApiUrl: e.target.value })}
                placeholder="https://msg.mobile-gw.com:9000"
              />
            </div>

            <div className="field">
              <label className="label">{t('Ime pošiljaoca', 'From Name (sender)')}</label>
              <input
                className="input"
                value={nth.nthFromName}
                onChange={(e) => setNth({ ...nth, nthFromName: e.target.value })}
                placeholder="Acme"
              />
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">{t('NTH ograničenje brzine (poruka/sek)', 'NTH Rate Limit (msgs/sec)')}</label>
              <input
                className="input"
                type="number"
                min={1}
                value={nth.nthRateLimit}
                onChange={(e) => setNth({ ...nth, nthRateLimit: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
            {saving ? t('Čuvanje…', 'Saving…') : t('Sačuvaj kredencijale', 'Save Credentials')}
          </button>
        </form>
      )}
    </div>
  );
};

export default TenantConfig;
